import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {KiLauf, KiLaufService, KiTeilLauf} from '../../../service/ki-lauf.service';

/**
 * Steht direkt neben einem Knopf "KI-Texte erzeugen" und zeigt, was dessen Anfrage gerade
 * tut: drehender Ring, Sekunden seit dem Klick und die Phase in Worten ("KI schreibt den
 * Text …", "Text wird gekürzt und überarbeitet …"). Am Ende eine grüne Lampe (fertig) oder
 * eine rote (Fehler, Zeitüberschreitung), mit der Dauer; die Ursache steht im Tooltip.
 *
 * Bleibt unsichtbar, solange der Knopf nicht gedrückt wurde. Welcher Knopf es ist, sagt
 * allein der {@link schluessel} – derselbe wie in `KiLaufService` (`GrafikUmfang.schluessel`
 * der Leiste, die den Knopf trägt).
 *
 * Alles hier liest Signale (`KiLaufService`), deshalb aktualisiert sich die Anzeige auch,
 * wenn die Änderung aus einem HTTP-Callback kommt.
 */
@Component({
  selector: 'app-ki-lauf-anzeige',
  templateUrl: './ki-lauf-anzeige.component.html',
  styleUrls: ['./ki-lauf-anzeige.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class KiLaufAnzeigeComponent {

  /** Schlüssel des Knopfes, dessen Lauf gezeigt wird. */
  @Input() schluessel: string | null = null;

  constructor(private kiLaufService: KiLaufService) {
  }

  get lauf(): KiLauf | undefined {
    return this.kiLaufService.laufFuer(this.schluessel);
  }

  get laeuft(): boolean {
    const lauf = this.lauf;
    return !!lauf && lauf.teile.length > 0 && lauf.ende === undefined;
  }

  get fehlgeschlagen(): boolean {
    return !!this.lauf?.teile.some(teil => teil.zustand === 'fehler');
  }

  /** Sekunden seit dem Klick – bis zum Ende mitlaufend, danach eingefroren. */
  get sekunden(): number {
    const lauf = this.lauf;
    if (!lauf) {
      return 0;
    }
    const bis = lauf.ende ?? this.kiLaufService.jetzt();
    return Math.max(0, Math.floor((bis - lauf.start) / 1000));
  }

  /** Mehrere Anfragen (Heute + Schlagzeile): dann nennt jede Zeile ihre Box. */
  get mehrere(): boolean {
    return (this.lauf?.teile.length ?? 0) > 1;
  }

  /** Text einer Anfrage in der Anzeige. */
  zeile(teil: KiTeilLauf): string {
    const text = teil.zustand === 'laeuft' ? teil.phase
      : teil.zustand === 'fertig' ? 'fertig'
        : `Fehler: ${teil.fehler ?? 'unbekannt'}`;
    return this.mehrere ? `${teil.box}: ${text}` : text;
  }

  /** Tooltip der Lampe: alle Teil-Ergebnisse samt Ursache. */
  get lampenTitel(): string {
    return (this.lauf?.teile ?? []).map(teil =>
      `${teil.box}: ${teil.zustand === 'fehler' ? 'Fehler – ' + (teil.fehler ?? 'unbekannt') : 'fertig'}`
    ).join('\n');
  }
}
